import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { 
  FiHome, 
  FiUsers, 
  FiDollarSign, 
  FiSettings, 
  FiLogOut, 
  FiMenu, 
  FiX,
  FiUserPlus,
  FiCreditCard,
  FiChevronDown,
  FiChevronRight,
  FiPieChart,
  FiBell
} from "react-icons/fi";
import { BiRupee } from 'react-icons/bi';
import { MdCurrencyRupee } from 'react-icons/md';
import { AiOutlineTransaction } from 'react-icons/ai';
import { BiHistory } from 'react-icons/bi';


const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get user ID from token when component mounts
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserId(payload.userId);
      } catch (error) {
        console.error("Error parsing token:", error);
      }
    }
  }, []);

  // Close mobile sidebar when route changes
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    navigate("/login");
  };

  const toggleItemExpansion = (path) => {
    setExpandedItems(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };
const allNavItems = [
  {
    path: "/dashboard",
    icon: FiHome,
    label: "Dashboard",
  },
  {
    path: "/transactions",
    icon: AiOutlineTransaction, // 👈 better for transactions
    label: "Transactions",
  },
  {
    path: "/user-management",
    icon: FiUsers,
    label: "User Management",
  },
  {
    path: "/withdrawals-management",
    icon: MdCurrencyRupee, // 👈 rupee icon for withdrawals
    label: "Withdrawals Management",
  },
  {
    path: "/recharge-history",
    icon: BiHistory, // 👈 history icon for recharge history
    label: "Recharge History",
  },
];



const limitedNavItems = [
  {
    path: "/user-management",
    icon: FiUsers,
    label: "User Management",
  },
  {
    path: "/recharge-history",
    icon: BiHistory,
    label: "Recharge History",
  },
];


  const navItems = userId === "d60e5499-d8c0-43db-9cdc-22f06c4910c0" 
    ? allNavItems 
    : limitedNavItems;

  // Close sidebar on mobile when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileSidebarOpen && !e.target.closest('.sidebar')) {
        setMobileSidebarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileSidebarOpen]);

  return (
    <div className="flex h-screen bg-gray-50 font-sans">
      {/* Mobile sidebar backdrop */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-30 lg:hidden transition-opacity duration-300"></div>
      )}

      {/* Sidebar */}
      <div
        className={`sidebar fixed lg:relative z-40 ${
          mobileSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } ${
          sidebarOpen ? "w-64" : "w-20"
        } bg-white shadow-xl transition-all duration-300 ease-in-out flex flex-col h-full border-r border-gray-200`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          {sidebarOpen && (
            <h1 className="text-xl font-bold text-gray-800 whitespace-nowrap">
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                AdminPanel
              </span>
            </h1>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-500 hover:text-gray-700 hidden lg:block"
          >
            {sidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="text-gray-500 hover:text-gray-700 lg:hidden"
          >
            <FiX size={24} />
          </button>
        </div>

        <nav className="mt-4 flex-1 overflow-y-auto">
          {navItems.map((item) => (
            <div key={item.path}>
              <div className="flex items-center">
                <NavLink
                  to={item.path}
                  className={({ isActive }) => 
                    `flex flex-1 items-center p-3 mx-2 rounded-lg transition-colors ${
                      isActive 
                        ? "bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-600 font-medium border-l-4 border-indigo-500" 
                        : "hover:bg-gray-50 text-gray-600 hover:text-gray-900"
                    }`
                  }
                >
                  <item.icon className={`mr-3 flex-shrink-0 ${sidebarOpen ? "" : "mx-auto"}`} size={20} />
                  {sidebarOpen && <span className="truncate">{item.label}</span>}
                </NavLink>

                {sidebarOpen && item.subItems && (
                  <button
                    onClick={() => toggleItemExpansion(item.path)}
                    className="p-2 mr-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                  >
                    {expandedItems[item.path] ? (
                      <FiChevronDown size={18} />
                    ) : (
                      <FiChevronRight size={18} />
                    )}
                  </button>
                )}
              </div>

              {/* Sub-items */}
              {sidebarOpen && item.subItems && expandedItems[item.path] && (
                <div className="ml-8 pl-3 border-l border-gray-200">
                  {item.subItems.map((subItem) => (
                    <NavLink
                      key={subItem.path}
                      to={subItem.path}
                      className={({ isActive }) => 
                        `flex items-center p-2.5 text-sm rounded-lg mx-2 transition-colors ${
                          isActive 
                            ? "bg-indigo-50 text-indigo-600 font-medium" 
                            : "hover:bg-gray-50 text-gray-600 hover:text-gray-900"
                        }`
                      }
                    >
                      <subItem.icon className="mr-3 flex-shrink-0" size={16} />
                      <span className="truncate">{subItem.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="flex items-center w-full p-2.5 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors group"
          >
            <FiLogOut className={`mr-3 flex-shrink-0 ${sidebarOpen ? "" : "mx-auto"} group-hover:text-red-500`} size={20} />
            {sidebarOpen && <span className="group-hover:text-red-500">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm z-20">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center">
              <button
                onClick={() => setMobileSidebarOpen(true)}
                className="text-gray-500 hover:text-gray-700 mr-4 lg:hidden"
              >
                <FiMenu size={24} />
              </button>
              <h2 className="text-xl font-semibold text-gray-800">
                {navItems.find(item => location.pathname.startsWith(item.path))?.label || "Dashboard"}
              </h2>
            </div>
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-500 hover:text-gray-700 relative hover:bg-gray-100 rounded-full transition-colors">
                <FiBell size={20} />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
              <div className="relative group">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-sm cursor-pointer">
                  <span className="font-medium">A</span>
                </div>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 hidden group-hover:block">
                  <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                    <div className="font-medium">Admin User</div>
                    <div className="text-gray-500 text-xs">admin@example.com</div>
                  </div>
                  <button 
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-red-500"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;